import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { html, css, LitElement, nothing } from 'lit';
import { when } from 'lit/directives/when.js';
import { until } from 'lit/directives/until.js';
import { ShouldUpdate } from '../../modules/ShouldUpdate.js';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import { collection, collectionGroup, getFirestore, onSnapshot, query, where } from 'firebase/firestore';
import { KatapultJob } from '../../modules/katapult-job/KatapultJob.js';
import { Path } from '../../modules/Path.js';
import '../katapult-elements/katapult-icon.js';
import '../katapult-elements/katapult-button.js';
import '../user-chip/user-chip.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/skeleton/skeleton.js';
import '@shoelace-style/shoelace/dist/components/relative-time/relative-time.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@lit-labs/virtualizer';
import { moveConversationToUserInbox, removeConversationFromUserInbox } from './Feedback.js';
import { capitalCase } from 'change-case';
import pluralize from 'pluralize-esm';
import { Parallel } from '../../modules/Parallel.js';
/** @import { Query, CollectionReference } from 'firebase/firestore' */

class FeedbackList extends LitElement {
  static properties = {
    inbox: { type: Boolean },
    issuesOnly: { type: Boolean },
    companyId: { type: String, shouldUpdate: false },
    userId: { type: String, shouldUpdate: false },
    jobId: { type: String, shouldUpdate: false },
    entityId: { type: String, shouldUpdate: false },
    entityType: { type: String, shouldUpdate: false },
    subEntityId: { type: String, shouldUpdate: false },
    subEntityType: { type: String, shouldUpdate: false },
    userFeedbackScope: { type: String, shouldUpdate: false },
    allowPublicFeedback: { type: Boolean, shouldUpdate: false },
    allowPrivateFeedback: { type: Boolean, shouldUpdate: false },

    _loadingPrivateFeedback: { type: Boolean },
    _loadingPublicFeedback: { type: Boolean },
    _privateFeedbackDocuments: { type: Array, shouldUpdate: false },
    _publicFeedbackDocuments: { type: Array, shouldUpdate: false },
    _curatedFeedbackDocuments: { type: Array },
    _deletedStatesForJobsWithFeedback: { type: Object, shouldUpdate: false },
    _currentUserUid: { type: String, shouldUpdate: false }
  };

  static styles = css`
    :host {
      min-height: 48px;
    }
    .feedbackRow {
      --feedback-icon-size: 20px;
      --feedback-border-width: 1px;
      --feedback-row-icon-color: var(--sl-color-sky-500);
      --feedback-row-icon-background-color: var(--sl-color-sky-100);
      --feedback-row-border-color: var(--sl-color-neutral-200);
      --feedback-row-border-radius: 12px;
      position: relative;
      display: flex;
      gap: var(--sl-spacing-x-small);
      padding: var(--sl-spacing-2x-small) var(--sl-spacing-2x-small) var(--sl-spacing-2x-small)
        calc(var(--feedback-icon-size) + var(--sl-spacing-small));
      height: 48px;
      width: -webkit-fill-available;
      border: var(--feedback-border-width) solid var(--feedback-row-border-color);
      border-radius: var(--feedback-row-border-radius);
      box-sizing: border-box;
      background: var(--sl-color-neutral-50);
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-800);
    }
    .feedbackRow[unread] {
      background: var(--sl-color-neutral-0);
    }
    .feedbackRow[unread] .label {
      font-weight: var(--sl-font-weight-bold);
    }
    .feedbackRow[variant='success'] {
      --feedback-row-icon-color: var(--sl-color-success-500);
      --feedback-row-icon-background-color: var(--sl-color-success-100);
    }
    .feedbackRow[variant='danger'] {
      --feedback-row-icon-color: var(--sl-color-danger-500);
      --feedback-row-icon-background-color: var(--sl-color-danger-100);
    }
    .feedbackRow:not(:last-child) {
      margin-bottom: var(--sl-spacing-2x-small);
    }
    .feedbackRow:hover {
      cursor: pointer;
      z-index: 1;
    }
    .feedbackRow:not(:hover) .buttons {
      display: none;
    }
    .feedbackRow .statusIconContainer {
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: var(--feedback-border-width);
      width: fit-content;
      background: var(--feedback-row-icon-background-color);
      border-radius: var(--feedback-row-border-radius) 0 var(--feedback-row-border-radius) 0;
    }
    .feedbackRow .statusIconContainer katapult-icon {
      color: var(--feedback-row-icon-color);
      border-radius: 100%;
    }
    .feedbackRow .curve {
      position: absolute;
      height: var(--feedback-icon-size);
      width: var(--feedback-icon-size);
      overflow: hidden;
    }
    .feedbackRow .convex {
      height: inherit;
      width: inherit;
      position: absolute;
      border-radius: 50% 0 0 0;
      outline: var(--feedback-icon-size) solid var(--feedback-row-icon-background-color);
    }
    .column {
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
    }
    .row,
    .label {
      display: flex;
      align-items: center;
    }
    .row {
      gap: var(--sl-spacing-2x-small);
    }
    .label {
      font-weight: var(--sl-font-weight-semibold);
      gap: var(--sl-spacing-3x-small);
    }
    .label.bold {
      font-weight: var(--sl-font-weight-bold);
    }
    sl-divider {
      --spacing: var(--sl-spacing-2x-small);
    }
    .ellipsis {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    sl-skeleton::part(base) {
      min-height: 10px;
    }
    .overlay {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .header {
      color: var(--sl-color-neutral-800);
      font-size: var(--sl-font-size-small);
      font-weight: bold;
      box-sizing: border-box;
      padding-top: var(--sl-spacing-small);
      margin-bottom: var(--sl-spacing-2x-small);
    }
  `;

  #renderFeedbackTemplate(feedback) {
    const { _currentUserUid } = this;
    const {
      _id: feedbackId,
      updated_by: updatedBy,
      updated_at: updatedAt,
      job_id: jobId,
      entity_type: entityType,
      entity_id: entityId,
      last_message: lastMessage,
      issue_status: issueStatus,
      unread_by: unreadBy,
      queued_for: queuedFor,
      subscribers,
      header,
      isPublic
    } = feedback ?? {};

    // Render a template for the header if the feedback item is a header
    if (header) return html`<div class="header"><span>${header}</span></div>`;

    // Build data to render the feedback item
    const variant = { UNRESOLVED: 'danger', RESOLVED: 'success' }[issueStatus];
    const icon = { danger: 'error', success: 'check_circle' }[variant] ?? 'info';
    const inConversation = subscribers?.includes(_currentUserUid);
    const inInbox = queuedFor?.includes(_currentUserUid);
    const unread = unreadBy?.includes(_currentUserUid);
    const buttonIconConfig = { fill: false };
    const updatedByLabel = this.#getUserLabel(updatedBy);
    const updatedAtISO = updatedAt?.toDate().toISOString();

    // Build the path to the feedback conversation
    const conversationRefPath = isPublic
      ? `jobs/${jobId}/public_feedback/${feedbackId}`
      : `companies/${this.companyId}/feedback/${feedbackId}`;

    // Build job name template.
    const jobNamePromise = this.#getJobName(jobId);
    const jobNameTemplate = jobNamePromise.then(
      (jobName) => html`
        <sl-tooltip content=${jobName} hoist>
          <div class="label ellipsis">${jobName ?? 'Unknown Job'}</div>
        </sl-tooltip>
      `
    );
    // Build entity label template.
    const entityLabelPromise = this.#getEntityLabel(jobId, entityType, entityId);
    const entityLabelTemplate = entityLabelPromise.then((entityLabel) =>
      when(
        entityLabel != null,
        () => html`
          <sl-tooltip content=${entityLabel} hoist>
            <div class="label ellipsis">${entityLabel}</div>
          </sl-tooltip>
        `
      )
    );
    // Placeholder template for labels which are loading.
    const skeletonTemplate = html`<sl-skeleton class="skeleton" style="max-width: 200px;"></sl-skeleton>`;
    return html`
      <div
        class="feedbackRow"
        variant=${variant}
        ?unread=${unread}
        @click=${this.#conversationClicked.bind(this, conversationRefPath, isPublic)}
      >
        <div class="statusIconContainer">
          <div class="curve" style="right: -20px; top: 0;"><div class="convex"></div></div>
          <div class="curve" style="bottom: -20px; left: 0;"><div class="convex"></div></div>
          <katapult-icon .icon=${icon} size="20"></katapult-icon>
        </div>
        <div class="column" style="min-width: 100px; width: 100px; overflow: hidden;">
          ${until(jobNameTemplate, skeletonTemplate)} ${until(entityLabelTemplate, skeletonTemplate)}
        </div>
        <sl-divider vertical></sl-divider>
        <user-chip .uid=${updatedBy} hide-name></user-chip>
        <div class="column" style="flex-grow: 1; overflow: hidden;">
          <div class="label ellipsis">${until(updatedByLabel, skeletonTemplate)}</div>
          <div class="row">
            <sl-tooltip content=${lastMessage} hoist>
              <div class="ellipsis" style="color: var(--sl-color-neutral-800);">${lastMessage}</div>
            </sl-tooltip>
            <div>•</div>
            ${updatedAtISO != null
              ? html`
                  <sl-tooltip hoist>
                    <div slot="content" class="row">
                      <sl-relative-time class="ellipsis" date=${updatedAtISO} sync></sl-relative-time>
                      <div>•</div>
                      <sl-format-date date=${updatedAtISO}></sl-format-date>
                    </div>
                    <sl-relative-time class="ellipsis" date=${updatedAtISO} sync></sl-relative-time>
                  </sl-tooltip>
                `
              : nothing}
          </div>
        </div>
        ${isPublic === true
          ? html`
              <sl-tag style="align-self: center" variant="neutral" pill>
                <div style="display: flex; align-items: center; justify-content: center; height: -webkit-fill-available;">
                  <katapult-icon icon="public" size="16" style="margin-right: var(--sl-spacing-2x-small);"></katapult-icon>
                  <span>Public</span>
                </div>
              </sl-tag>
            `
          : nothing}
        <div class="row buttons" style="flex-shrink: 0;">
          <sl-divider vertical></sl-divider>
          <div class="row">
            ${when(
              inConversation,
              () => html`
                ${when(
                  inInbox,
                  () => html`
                    <sl-tooltip content="Remove from Inbox" hoist>
                      <katapult-button
                        iconOnly
                        size="20"
                        icon="archive"
                        noBorder
                        .iconConfig=${buttonIconConfig}
                        @click=${(e) => e.stopPropagation()}
                        .callback=${this.#removeConversationFromInbox.bind(this, conversationRefPath)}
                      ></katapult-button>
                    </sl-tooltip>
                  `,
                  () => html`
                    <sl-tooltip content="Move to Inbox" hoist>
                      <katapult-button
                        iconOnly
                        size="20"
                        icon="unarchive"
                        noBorder
                        .iconConfig=${buttonIconConfig}
                        @click=${(e) => e.stopPropagation()}
                        .callback=${this.#moveConversationToInbox.bind(this, conversationRefPath)}
                      ></katapult-button>
                    </sl-tooltip>
                  `
                )}
              `
            )}
          </div>
        </div>
      </div>
    `;
  }

  render() {
    const { _loadingPrivateFeedback, _loadingPublicFeedback, _curatedFeedbackDocuments } = this;
    const noFeedback = !_loadingPrivateFeedback && !_loadingPublicFeedback && _curatedFeedbackDocuments.length === 0;
    return html`
      ${when(
        noFeedback,
        () => html`
          <div class="overlay">
            <div class="row" style="color: var(--sl-color-neutral-800);">
              <katapult-icon icon="speaker_notes_off"></katapult-icon>
              <div>You have no Feedback</div>
            </div>
          </div>
        `,
        () => html`
          <lit-virtualizer
            .items=${this._curatedFeedbackDocuments}
            .renderItem=${this.#renderFeedbackTemplate.bind(this)}
            .keyFunction=${(item) => item.id}
          ></lit-virtualizer>
        `
      )}
    `;
  }

  constructor() {
    super();
    /** @type {Boolean} */
    this.inbox = false;
    /** @type {?string} */
    this.companyId = null;
    /** @type {?string} */
    this.userId = null;
    /** @type {?string} */
    this.jobId = null;
    /** @type {?string} */
    this.entityId = null;
    /** @type {?string} */
    this.entityType = null;
    /** @type {?string} */
    this.subEntityId = null;
    /** @type {?string} */
    this.subEntityType = null;
    /** @type {?string} */
    this.userFeedbackScope = 'inbox';
    /** @type {boolean} */
    this.issuesOnly = false;
    /** @type {?Boolean} */
    this.allowPublicFeedback = false;
    /** @type {?Boolean} */
    this.allowPrivateFeedback = false;

    /** @type {?string} */
    this._currentUserUid = null;
    /** @type { Record<string, Promise<any>>} */
    this._labelLookup = {};
    /** @type {Boolean} */
    this._loadingPrivateFeedback = false;
    /** @type {Boolean} */
    this._loadingPublicFeedback = false;
    /** @type {object[]} */
    this._privateFeedbackDocuments = [];
    /** @type {object[]} */
    this._publicFeedbackDocuments = [];
    /** @type {object[]} */
    this._curatedFeedbackDocuments = [];
    /** @type {object} */
    this._deletedStatesForJobsWithFeedback = {};

    onIdTokenChanged(getAuth(), async (user) => {
      this._currentUserUid = user?.uid ?? null;
    });
  }

  shouldUpdate(changedProperties) {
    // Update the public and private feedback queries
    const queryProperties = ['userId', 'jobId', 'entityId', 'subEntityId'];
    if (['companyId', 'inbox', 'allowPrivateFeedback', ...queryProperties].some((prop) => changedProperties.has(prop))) {
      if (this.allowPrivateFeedback === true || this.inbox) this.#updatePrivateFeedbackQuery();
    }
    if (['allowPublicFeedback', 'inbox', ...queryProperties].some((prop) => changedProperties.has(prop))) {
      // Only update the public feedback query if public feedback should be included
      if (this.allowPublicFeedback === true || this.inbox) this.#updatePublicFeedbackQuery();
    }

    // Curate the list of feedback documents to show to the user
    if (
      ['_privateFeedbackDocuments', '_publicFeedbackDocuments', 'inbox', '_currentUserUid', 'userFeedbackScope', 'issuesOnly'].some(
        (prop) => changedProperties.has(prop)
      )
    ) {
      const privateFeedbackDocuments = this._privateFeedbackDocuments ?? [];
      const publicFeedbackDocuments = this._publicFeedbackDocuments ?? [];
      const allFeedbackDocuments = [...privateFeedbackDocuments, ...publicFeedbackDocuments];
      this.#setCuratedFeedbackDocuments(allFeedbackDocuments);
    }

    return ShouldUpdate(this, changedProperties);
  }

  #filterUserInbox(feedbackDocuments) {
    const { _currentUserUid, userFeedbackScope } = this;
    if (userFeedbackScope == 'inbox') return feedbackDocuments.filter((doc) => doc.queued_for?.includes(_currentUserUid));
    return feedbackDocuments;
  }

  /**
   * Sorts, filters, and groups feedback documents based on a number of criteria.
   * @param {object[]} allFeedbackDocuments - The feedback documents to curate.
   */
  async #setCuratedFeedbackDocuments(allFeedbackDocuments) {
    const { inbox } = this;
    // Filter out feedback documents that are missing updated_at or last_message
    const validFeedbackDocuments = allFeedbackDocuments.filter((doc) => doc.updated_at != null && doc.last_message != null);
    // Filter for issue status
    const feedbackDocumentsAfterIssuesFilter = validFeedbackDocuments.filter(
      (doc) => this.issuesOnly === false || doc.issue_status === 'UNRESOLVED'
    );

    // Filter out feedback documents that are not in the user's inbox if the inbox is enabled
    const filteredFeedbackDocuments = inbox
      ? this.#filterUserInbox(feedbackDocumentsAfterIssuesFilter)
      : feedbackDocumentsAfterIssuesFilter;

    // Identify and remove deleted jobs
    await this.#identifyDeletedJobsWithFeedback(filteredFeedbackDocuments);
    const existingJobs = filteredFeedbackDocuments.filter((doc) => this._deletedStatesForJobsWithFeedback[doc.job_id] === false);

    // Sort feedback documents based on if the current list is an inbox or not
    let curatedFeedbackDocuments;
    if (inbox) curatedFeedbackDocuments = existingJobs.sort((a, b) => sortConversationsByUpdateTime(a, b));
    else curatedFeedbackDocuments = this.#groupAndSortFeedbackDocuments(filteredFeedbackDocuments);
    this._curatedFeedbackDocuments = curatedFeedbackDocuments;
  }

  /**
   * Evaluates the feedback documents to identify and deleted jobs to a property
   * @param {object[]} feedbackDocuments - The feedback documents to check for deleted jobs.
   */
  async #identifyDeletedJobsWithFeedback(feedbackDocuments) {
    // Check if the jobs associated with the feedback documents have already been evaluated
    const unevaluatedFeedbackDocuments = feedbackDocuments.filter(
      (doc) => doc.job_id && this._deletedStatesForJobsWithFeedback[doc.job_id] === undefined
    );
    // Evaluate the jobs associated with the feedback documents
    await Parallel.forEach(
      unevaluatedFeedbackDocuments,
      async (feedbackDocument) => {
        // Get the deleted status of the job
        const jobId = feedbackDocument.job_id;
        const deleted = (await globalThis.FirebaseWorker.ref(`photoheight/jobs/${jobId}/deleted`).once('value')).val();
        this._deletedStatesForJobsWithFeedback[jobId] = deleted ?? false;
      },
      10
    );
  }

  /**
   * Builds a Firestore query to fetch feedback based on various parameters.
   * @param {CollectionReference|Query} feedbackRef - The reference to the feedback.
   * @returns {Query|undefined} - A Firestore query object if conditions are met, otherwise undefined.
   */
  #buildFeedbackQuery(feedbackRef) {
    const { userId, jobId, entityId, subEntityId } = this;
    const andExpressions = [];
    if (userId) andExpressions.push(where('subscribers', 'array-contains', userId));
    if (jobId) andExpressions.push(where('job_id', '==', jobId));
    if (entityId && entityId !== jobId) andExpressions.push(where('entity_id', '==', entityId));
    if (subEntityId) andExpressions.push(where('subentity_id', '==', subEntityId));
    if (andExpressions.length === 0) return;

    const feedbackQuery = query(feedbackRef, ...andExpressions);
    return feedbackQuery;
  }

  /**
   * Setup a listener for private feedback documents
   * @returns {void}
   */
  #updatePrivateFeedbackQuery() {
    this.privateFeedbackQueryDisconnect?.();

    // Build the feedback query
    const { companyId } = this;
    if (!companyId) return;
    const feedbackRef = collection(getFirestore(), `companies/${companyId}/feedback`);
    const feedbackQuery = this.#buildFeedbackQuery(feedbackRef);
    if (!feedbackQuery) return;

    // Listen for changes to the feedback documents
    const unsubscribe = onSnapshot(feedbackQuery, (snapshot) => {
      this._privateFeedbackDocuments = Array.from(snapshot.docs).map((doc) => {
        const data = doc.data();
        return /** @type {object} */ ({ ...data, _id: doc.id, _ref: doc.ref });
      });

      this._loadingPrivateFeedback = false;
    });

    this.privateFeedbackQueryDisconnect = () => {
      unsubscribe();
      this._privateFeedbackDocuments = [];
      this._loadingPrivateFeedback = false;
      this.privateFeedbackQueryDisconnect = null;
    };

    this._loadingPrivateFeedback = true;
  }

  /**
   * Setup a listener for public feedback documents
   * @returns {void}
   */
  #updatePublicFeedbackQuery() {
    this.publicFeedbackQueryDisconnect?.();

    // Build the feedback query
    const feedbackRef = this.inbox
      ? collectionGroup(getFirestore(), 'public_feedback')
      : collection(getFirestore(), `jobs/${this.jobId}/public_feedback`);
    const feedbackQuery = this.#buildFeedbackQuery(feedbackRef);
    if (!feedbackQuery) return;

    // Listen for changes to the feedback documents
    const unsubscribe = onSnapshot(feedbackQuery, (snapshot) => {
      this._publicFeedbackDocuments = Array.from(snapshot.docs).map((doc) => {
        const data = doc.data();
        return /** @type {object} */ ({ ...data, _id: doc.id, _ref: doc.ref, isPublic: true });
      });

      this._loadingPublicFeedback = false;
    });

    this.publicFeedbackQueryDisconnect = () => {
      unsubscribe();
      this._publicFeedbackDocuments = [];
      this._loadingPublicFeedback = false;
      this.publicFeedbackQueryDisconnect = null;
    };

    this._loadingPublicFeedback = true;
  }

  /**
   * Groups feedback documents by entity type and sub type, sorts them by their latest update time, and concatenates them into a single array.
   * @param {object[]} feedbackDocuments - The feedback documents to group and sort.
   * @returns {object[]} - The grouped and sorted feedback documents.
   */
  #groupAndSortFeedbackDocuments(feedbackDocuments) {
    // Group the feedback documents by entity type
    const feedbackDocumentsByType = {};
    (feedbackDocuments ?? []).forEach((feedbackDocument) => {
      const entityType = feedbackDocument.entity_type;
      const subEntityType = feedbackDocument.subentity_type;
      const feedbackType = [entityType, subEntityType].filter((x) => !!x).join(':');
      feedbackDocumentsByType[feedbackType] ??= [];
      feedbackDocumentsByType[feedbackType].push(feedbackDocument);
    });

    // Transform the feedback documents by type to an array of groups
    const feedbackGroups = Object.entries(feedbackDocumentsByType).map(([feedbackType, documents]) => {
      // Sort the documents by their latest update time
      const sortedDocuments = documents.sort((a, b) => sortConversationsByUpdateTime(a, b));

      // Get the label for the group (based on the feedback type)
      let label = feedbackType
        .split(':')
        .map((x) => capitalCase(x))
        .join(' ');
      // Change the label for attribute events
      if (feedbackType.includes(':ATTRIBUTE_EVENT')) {
        const entityName = feedbackType.split(':')[0];
        label = `${capitalCase(entityName ?? 'Item')} Attributes`;
      }

      return { feedbackType, groupLabel: pluralize(label), documents: sortedDocuments };
    });

    // Sort the feedback documents by type and sub type.
    const sortedFeedbackGroups = feedbackGroups.sort((feedbackGroupA, feedbackGroupB) => {
      const aSortValue = this.getSortValueForFeedbackGroup(feedbackGroupA);
      const bSortValue = this.getSortValueForFeedbackGroup(feedbackGroupB);
      return aSortValue - bSortValue;
    });

    // Concatenate the feedback documents into a single array with headers
    const groupedAndSortedFeedbackDocuments = [];
    sortedFeedbackGroups.forEach((feedbackGroup) => {
      const { groupLabel, documents } = feedbackGroup;
      // Exclude the header for the current type
      if (feedbackGroup.feedbackType !== this.entityType) groupedAndSortedFeedbackDocuments.push({ header: groupLabel });
      groupedAndSortedFeedbackDocuments.push(...documents);
    });

    return groupedAndSortedFeedbackDocuments;
  }

  /**
   * Get the sort value for a feedback group.  Sort the current entity type first, then job feedback groups first (job actions and attributes), then
   * groups with no sub type (node), then groups with sub types (node attributes and actions).
   * @param {object} feedbackGroup - The feedback group to get the sort value for.
   * @returns {number} - The sort value for the feedback group.
   */
  getSortValueForFeedbackGroup(feedbackGroup) {
    const { feedbackType } = feedbackGroup;
    switch (feedbackType) {
      case 'JOB':
        return 0;
      case 'JOB:ATTRIBUTE_EVENT':
        return 1;
      case 'JOB:ACTION':
        return 2;
      case 'NODE':
        return 3;
      case 'NODE:ATTRIBUTE_EVENT':
        return 4;
      case 'NODE:ACTION':
        return 5;
      default:
        return 6;
    }
  }

  /**
   * Fetches and caches the user info or returns the already cached promise.
   * @param {string} uid
   * @returns {Promise<any>}
   */
  #getUserLabel(uid) {
    const { _labelLookup: labelLookup } = this;
    const userLabelPromise = (labelLookup[uid] ??= globalThis.FirebaseWorker.ref(`user_info/${uid}`)
      .once('value')
      .then((snapshot) => {
        const userInfo = snapshot.val() ?? {};
        const name = [userInfo.name?.first, userInfo.name?.last].filter((x) => !!x).join(' ');
        return name || userInfo.email || uid;
      }));
    return userLabelPromise;
  }

  /**
   * Fetches and caches the job name or returns the already cached promise.
   * @param {string} jobId
   * @returns {Promise<any>}
   */
  async #getJobName(jobId) {
    const { _labelLookup: labelLookup } = this;
    const jobNamePromise = (labelLookup[jobId] ??= KatapultJob.fromId(jobId, { noCache: true }).getName());
    return jobNamePromise;
  }

  /**
   * Fetches and caches the entity label or returns the already cached promise.
   * @param {string} jobId
   * @param {string} entityType
   * @param {string} entityId
   * @returns {Promise<any>}
   */
  async #getEntityLabel(jobId, entityType, entityId) {
    const { _labelLookup: labelLookup } = this;
    switch (entityType) {
      case 'NODE': {
        const entityLabelPromise = (labelLookup[entityId] ??= KatapultJob.fromId(jobId, { noCache: true })
          .getNodeById(entityId)
          .then((node) => {
            const scid = Path.get(node?.attributes, 'scid.*');
            return scid ? `SCID ${scid}` : 'Unknown SCID';
          }));
        return entityLabelPromise;
      }
      default:
        return null;
    }
  }

  #conversationClicked(conversationRefPath) {
    this.dispatchEvent(new CustomEvent('conversation-selected', { detail: { conversationRefPath }, bubbles: true, composed: true }));
  }

  async #moveConversationToInbox(conversationRefPath) {
    if (conversationRefPath == null || this._currentUserUid == null) return;
    const conversationRef = firebase.firestore().doc(conversationRefPath);
    await moveConversationToUserInbox(conversationRef, this._currentUserUid);
  }

  async #removeConversationFromInbox(conversationRefPath) {
    if (conversationRefPath == null || this._currentUserUid == null) return;
    const conversationRef = firebase.firestore().doc(conversationRefPath);
    await removeConversationFromUserInbox(conversationRef, this._currentUserUid);
  }
}

customElements.define('feedback-list', FeedbackList);

function sortConversationsByUpdateTime(a, b) {
  const aTime = a.updated_at?.toMillis();
  const bTime = b.updated_at?.toMillis();

  if (aTime === bTime) return 0;
  // Sort nulls to the end
  if (aTime == null) return 1;
  if (bTime == null) return -1;
  // Sort by most recent first
  return bTime - aTime;
}
